import type { SchemaAndLayoutSchema } from '@unifyapps/network/generated/models/schemaAndLayoutSchema';
import type { UIFilter } from '@unifyapps/network/generated/models/uIFilter';
import type { CSSProperties } from 'react';
import type {
  BlockComponentStateId,
  BlockComponentWithRequiredAppearance,
} from '../../types/block';
import type { FieldKey } from '../../types/blocks';
import type {
  ConditionalValueFilters,
  ValueOrCondition,
} from '../../types/conditionalValueFilters';
import type { ConditionEvaluationObject } from '../../types/conditionEvaluationObject';
import type { SlotType } from '../../types/slot';
import type { LCColorValue, StyleObject } from '../../types/styleObject';
import type {
  DecorationColors,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
} from '../../types/typography';

export type { FieldKey };

// copied from rjsf utils because we do not want to add that package in defs
/** The type for error produced by RJSF schema validation */
export type RJSFValidationError = {
  /** Name of the error, for example, "required" or "minLength" */
  name?: string;
  /** Message, for example, "is a required property" or "should NOT be shorter than 3 characters" */
  message?: string;
  /** An object with the error params returned by ajv
   * ([see doc](https://github.com/ajv-validator/ajv/tree/6a671057ea6aae690b5967ee26a0ddf8452c6297#error-parameters)
   * for more info)
   */
  params?: unknown;
  /** A string in Javascript property accessor notation to the data path of the field with the error. For example,
   * `.name` or `['first-name']`
   */
  property?: string;
  /** JSON pointer to the schema of the keyword that failed validation. For example, `#/fields/firstName/required`.
   * (Note: this may sometimes be wrong due to a [bug in ajv](https://github.com/ajv-validator/ajv/issues/512))
   */
  schemaPath?: string;
  /** Full error name, for example ".name is a required property" */
  stack: string;
};

export type Schema = {
  schema: SchemaAndLayoutSchema;
  layout: SchemaAndLayoutSchema;
};

export type ValidationRule = {
  id: string;
  conditions: {
    type: 'filter';
    payload: UIFilter;
  };
  errorMessage: string;
};

export type ComputedValidationRule = {
  id: string;
  conditions: boolean;
  errorMessage: string;
};

export type ValidationRules = ValidationRule[];
export type ComputedValidationRules = ComputedValidationRule[];

export enum FormFieldTypes {
  Manual = 'manual',
  Mapped = 'mapped',
}

export enum FormSource {
  Entity = 'entity',
  Custom = 'custom',
  JsonSchema = 'json_schema',
}

export enum FormFieldErrorType {
  EmptyMappedFormFields = 'emptyMappedFormFields',
  NoMappedFormFields = 'noMappedFormFields',
}

export type DefaultFormContent = {
  readonly?: boolean;
  autocomplete?: 'on' | 'off';
  initialFormData?: Record<string, unknown>;
  // undefined means true, so in case of undefined reset the form after submit
  resetFormWhenHidden?: boolean;
  schema?: {
    schema: SchemaAndLayoutSchema;
    layout: SchemaAndLayoutSchema;
  };
};

type EntityFormContent = DefaultFormContent & {
  source: FormSource.Entity;
  action?: 'create' | 'update';
  entity?: string;
  entityId?: string;
  schema?: Schema;
};

export type MappedCustomFormContent = DefaultFormContent & {
  source: FormSource.Custom;
  mappedFields: {
    data?: string;
    fieldId?: FieldKey;
    fieldLabel?: FieldKey;
    fieldType?: FieldKey;
    description?: FieldKey;
    helpText?: FieldKey;
    defaultValue?: FieldKey;
    isOptional?: FieldKey;
    additionalFields?: {
      CHECKBOX?: {
        options?: FieldKey;
      };
      CODE_EDITOR?: {
        language?: FieldKey;
        expandable?: FieldKey;
      };
      DATE?: {
        disablePast?: FieldKey;
        dateTimeAddOns?: {
          dateSelectionLimits?: {
            dateRangeLimit?: FieldKey;
            includeToday?: FieldKey;
            minDate?: FieldKey;
            maxDate?: FieldKey;
          };
        };
      };
      DATE_TIME?: {
        disablePast?: FieldKey;
        dateTimeAddOns?: {
          dateSelectionLimits?: {
            dateRangeLimit?: FieldKey;
            includeToday?: FieldKey;
            minDate?: FieldKey;
            maxDate?: FieldKey;
          };
        };
      };
      EMAIL?: {
        placeholder?: FieldKey;
      };
      FILE_UPLOADER?: {
        multipleFiles?: FieldKey;
        supportedFileTypes?: FieldKey;
        maxFiles?: FieldKey;
        maxFileSize?: FieldKey;
      };
      PHONE_NUMBER?: {
        placeholder?: FieldKey;
        countryCode?: {
          showCountryCode?: FieldKey;
          showCountryFlag?: FieldKey;
          flagStyle?: FieldKey;
          isConfigurable?: FieldKey;
        };
      };
      CURRENCY_INPUT?: {
        placeholder?: FieldKey;
        currencyCode?: {
          isConfigurable?: FieldKey;
        };
      };
      INTEGER?: {
        placeholder?: FieldKey;
        minimum?: FieldKey;
        maximum?: FieldKey;
      };
      JSON_SCHEMA?: {
        allowedFieldTypes?: FieldKey;
        appearance?: {
          emptyState?: {
            description?: FieldKey;
          };
        };
      };
      KEY_VALUE_PAIR?: {
        fieldOptions?: {
          emptyState?: {
            description?: FieldKey;
          };
        };
      };
      MULTI_SELECT_DROPDOWN?: {
        placeholder?: FieldKey;
        optionsType?: FieldKey;
        options?: FieldKey;
        foreignKeyReference?: FieldKey;
        lookupLabel?: FieldKey;
      };
      NUMBER?: {
        placeholder?: FieldKey;
        minimum?: FieldKey;
        maximum?: FieldKey;
        numberAddOns?: {
          autoFormat?: {
            formatType?: FieldKey;
          };
          decimal?: {
            decimalPlaces?: FieldKey;
            padDecimalPlaces?: FieldKey;
          };
        };
      };
      OTP?: {
        placeholder?: FieldKey;
        otp?: {
          length?: FieldKey;
          masked?: FieldKey;
          variant?: FieldKey;
          maskingCharacter?: FieldKey;
          justifyContent?: FieldKey;
          fontSize?: FieldKey;
          styles?: FieldKey;
        };
      };
      PASSWORD?: {
        placeholder?: FieldKey;
        autocomplete?: FieldKey;
      };
      RADIO?: {
        options?: FieldKey;
      };
      RICH_TEXT?: {
        richTextAddOns?: {
          toolbar?: {
            variant?: FieldKey;
          };
        };
        minNumberOfLines: FieldKey;
        maxNumberOfLines: FieldKey;
      };
      SINGLE_SELECT_DROPDOWN?: {
        placeholder?: FieldKey;
        optionsType?: FieldKey;
        options?: FieldKey;
        foreignKeyReference?: FieldKey;
        lookupLabel?: FieldKey;
      };
      SLIDER?: {
        minimum?: FieldKey;
        maximum?: FieldKey;
        multipleOf?: FieldKey;
        showNumberInput?: FieldKey;
      };
      STRING?: {
        placeholder?: FieldKey;
        textFieldAddOns?: {
          prefixIcon?: FieldKey;
          suffixIcon?: FieldKey;
        };
      };
      TEXTAREA?: {
        placeholder?: FieldKey;
        maxRows?: FieldKey;
        minRows?: FieldKey;
        maxCharacters?: FieldKey;
      };
      TIME?: {
        timeStepSize?: FieldKey;
      };
      RATING?: {
        maxRating?: FieldKey;
        ratingIcon?: FieldKey;
      };
    };
  };
  fieldsType: FormFieldTypes.Mapped;
};

export type ManualCustomFormContent = DefaultFormContent & {
  source: FormSource.Custom;
  schema?: Schema;
  fieldsType: FormFieldTypes.Manual;
};

type CustomFormContent = MappedCustomFormContent | ManualCustomFormContent;

export type JsonSchemaFormContent = DefaultFormContent & {
  source: FormSource.JsonSchema;
  jsonSchema?: string | Record<string, unknown>;
  uiSchema?: string | Record<string, unknown>;
  defaultFormData?: string | Record<string, unknown>;
};

export type FormContent = EntityFormContent | CustomFormContent | JsonSchemaFormContent;

export type FormAppearance = {
  layout?: 'column' | 'row';
  autoFocus?: boolean;
  disabled?: ConditionEvaluationObject;
  readOnly?: ConditionEvaluationObject;
  fieldTitle?: {
    weight?: ValueOrCondition<TypographyWeights>;
    variant?: ValueOrCondition<TypographyVariants>;
    color: ValueOrCondition<LCColorValue<TypographyColors>>;
  };
  fieldRequired?: {
    weight?: ValueOrCondition<TypographyWeights>;
    variant?: ValueOrCondition<TypographyVariants>;
    color: ValueOrCondition<TypographyColors>;
  };
  fieldDescription?: DescriptionConfig;
  styles?: Pick<
    StyleObject,
    | 'padding'
    | 'maxHeight'
    | 'minHeight'
    | 'width'
    | 'minWidth'
    | 'maxWidth'
    | 'height'
    | 'overflow'
    | 'gap'
  >;
};

export type ComputedFormAppearance = FormAppearance & {
  disabled?: boolean;
  readOnly?: boolean;
};

export type FormSlots = Record<string, SlotType | undefined>;

export type FormComponentType = BlockComponentWithRequiredAppearance<
  'Form',
  FormContent,
  FormAppearance,
  FormSlots
>;

export type FormBlockStateType = Pick<FormComponentType, 'appearance' | 'componentType' | 'slots'> &
  BlockComponentStateId & {
    data: Record<string, unknown> | undefined;
    fieldVsDefaultValueMap: Record<string, unknown>;
    content: FormComponentType['content'] & {
      schema: Schema;
    };
    errors?: RJSFValidationError[];
  };

export type ComputedFormBlockStateType = Omit<FormBlockStateType, 'appearance'> & {
  appearance: ComputedFormAppearance;
};

export enum FieldTypeVsCustomStyleKey {
  PASSWORD = 'passwordField',
  PININPUT = 'pinInputField',
  TEXTINPUT = 'textInputField',
  CHECKBOX = 'checkboxField',
  DATE = 'dateField',
  DATE_TIME = 'dateTimeField',
  PHONE_NUMBER = 'phoneNumberField',
  CURRENCY_INPUT = 'currencyInputField',
  MULTI_SELECT_DROPDOWN = 'multiSelectField',
  SINGLE_SELECT_DROPDOWN = 'singleSelectField',
  TEXT_AREA = 'textAreaField',
}

export enum FormCustomStyleKeys {
  Asterisk = 'asterisk',
  FieldError = 'fieldError',
}

export enum FieldErrorCustomStyleKeys {
  Root = 'root',
  ErrorText = 'errorText',
}

export type FormCustomStyle = CSSProperties & {
  [FormCustomStyleKeys.Asterisk]: CSSProperties;
  [FormCustomStyleKeys.FieldError]: {
    [FieldErrorCustomStyleKeys.Root]: CSSProperties;
    [FieldErrorCustomStyleKeys.ErrorText]: CSSProperties;
  };
};

// TextArea
export enum TextAreaFieldCustomStyleKeys {
  Input = 'input',
}

export type TextAreaCustomStyle = CSSProperties & {
  [TextAreaFieldCustomStyleKeys.Input]: CSSProperties;
};

// PinInput
export enum PinInputFieldCustomStyleKeys {
  Input = 'input',
}

export type PinInputCustomStyle = CSSProperties & {
  [PinInputFieldCustomStyleKeys.Input]: CSSProperties;
};

// Password
export enum PasswordFieldCustomStyleKeys {
  Icon = 'icon',
  IconButton = 'iconButton',
}

// Checkbox
export enum CheckboxFieldCustomStyleKeys {
  Icon = 'icon',
  Checked = 'checked',
  Unchecked = 'unchecked',
  Indeterminate = 'indeterminate',
}

export type CheckboxCustomStyle = CSSProperties & {
  [CheckboxFieldCustomStyleKeys.Icon]: CSSProperties;
  [CheckboxFieldCustomStyleKeys.Checked]: CSSProperties;
  [CheckboxFieldCustomStyleKeys.Unchecked]: CSSProperties;
};

// Date and DateTime
export enum DateFieldCustomStyleKeys {
  StartIcon = 'startIcon',
  Input = 'input',
}

export type DateCustomStyle = CSSProperties & {
  [DateFieldCustomStyleKeys.StartIcon]: CSSProperties;
  [DateFieldCustomStyleKeys.Input]: CSSProperties;
};

// PhoneNumberInput
export enum PhoneNumberInputFieldCustomStyleKeys {
  Input = 'input',
  CountryCode = 'countryCode',
  CountryFlag = 'countryFlag',
  Divider = 'divider',
  BottomSheetHeader = 'bottomSheetHeader',
  OptionDivider = 'optionDivider',
}

export type PhoneNumberInputCustomStyle = CSSProperties & {
  [PhoneNumberInputFieldCustomStyleKeys.Input]: CSSProperties;
  [PhoneNumberInputFieldCustomStyleKeys.CountryCode]: CSSProperties;
  [PhoneNumberInputFieldCustomStyleKeys.CountryFlag]: CSSProperties;
  [PhoneNumberInputFieldCustomStyleKeys.Divider]: CSSProperties;
  [PhoneNumberInputFieldCustomStyleKeys.BottomSheetHeader]: CSSProperties;
  [PhoneNumberInputFieldCustomStyleKeys.OptionDivider]: CSSProperties;
};

// SingleSelectDropdown & MultiSelectDropdown
export enum DropdownFieldCustomStyleKeys {
  Input = 'input',
  SearchBar = 'searchBar',
  OptionLabel = 'optionLabel',
  SelectedPill = 'selectedPill',
  BottomSheet = 'bottomSheet',
  Dropdown = 'dropdown',
  GroupedOptions = 'groupedOptions',
  OptionCheckbox = 'optionCheckbox',
}

export enum SearchBarCustomStyleKeys {
  SearchIcon = 'searchIcon',
  Input = 'input',
}

export enum GroupedOptionsCustomStyleKeys {
  // root element of each Section or eachOption
  Root = 'root',

  Label = 'label',
  Selected = 'selected',
  Unselected = 'unselected',

  Section = 'section',
  SectionItems = 'sectionItems',
}

export enum BottomSheetCustomStyleKeys {
  Root = 'root',
  Header = 'header',
  Title = 'title',
  OptionsDivider = 'optionsDivider',
}

export type DropdownCustomStyle = CSSProperties & {
  [DropdownFieldCustomStyleKeys.Input]: CSSProperties;
  [DropdownFieldCustomStyleKeys.SearchBar]: CSSProperties & {
    [SearchBarCustomStyleKeys.SearchIcon]: CSSProperties;
    [SearchBarCustomStyleKeys.Input]: CSSProperties;
  };
  [DropdownFieldCustomStyleKeys.OptionLabel]: CSSProperties;
  [DropdownFieldCustomStyleKeys.OptionCheckbox]: CSSProperties;
  [DropdownFieldCustomStyleKeys.SelectedPill]: CSSProperties;
  [DropdownFieldCustomStyleKeys.BottomSheet]: {
    [BottomSheetCustomStyleKeys.Root]: CSSProperties;
    [BottomSheetCustomStyleKeys.Header]: {
      [BottomSheetCustomStyleKeys.Root]: CSSProperties;
      [BottomSheetCustomStyleKeys.Title]: CSSProperties;
    };
    [BottomSheetCustomStyleKeys.OptionsDivider]: CSSProperties;
  };
  [DropdownFieldCustomStyleKeys.Dropdown]: CSSProperties;
  [DropdownFieldCustomStyleKeys.GroupedOptions]: {
    [GroupedOptionsCustomStyleKeys.Section]: {
      [GroupedOptionsCustomStyleKeys.Root]: CSSProperties;
      [GroupedOptionsCustomStyleKeys.Label]: CSSProperties;
    };
    [GroupedOptionsCustomStyleKeys.SectionItems]: {
      [GroupedOptionsCustomStyleKeys.Root]: CSSProperties;
      [GroupedOptionsCustomStyleKeys.Label]: {
        [GroupedOptionsCustomStyleKeys.Selected]: CSSProperties;
        [GroupedOptionsCustomStyleKeys.Unselected]: CSSProperties;
      };
    };
  };
};

// CurrencyInput
export enum CurrencyInputFieldCustomStyleKeys {
  Input = 'input',
  CurrencyCode = 'currencyCode',
  Divider = 'divider',
  BottomSheetHeader = 'bottomSheetHeader',
  OptionDivider = 'optionDivider',
}

export type CurrencyInputCustomStyle = CSSProperties & {
  [CurrencyInputFieldCustomStyleKeys.Input]: CSSProperties;
  [CurrencyInputFieldCustomStyleKeys.CurrencyCode]: CSSProperties;
  [CurrencyInputFieldCustomStyleKeys.Divider]: CSSProperties;
  [CurrencyInputFieldCustomStyleKeys.BottomSheetHeader]: CSSProperties;
  [CurrencyInputFieldCustomStyleKeys.OptionDivider]: CSSProperties;
};

// Description Variants
export const DescriptionVariant = {
  Inline: 'inline',
  DashedTooltip: 'dashed-tooltip',
  IconTooltip: 'icon-tooltip',
} as const;

export type DescriptionVariantType = (typeof DescriptionVariant)[keyof typeof DescriptionVariant];

export interface InlineDescriptionConfig {
  variant: typeof DescriptionVariant.Inline;
}

export interface DashedTooltipDescriptionConfig {
  variant: typeof DescriptionVariant.DashedTooltip;
  /** dashed underline color. Stored as a DecorationColors token (e.g. 'decoration-warning-primary') selected via
   * decorationColorDetails picker, or a custom hex object { custom: '#hex' }. Applied directly as a
   * Tailwind decoration-* className in useUiSchemaWithGlobalStyles — no runtime CSS-variable lookup needed. */
  dashedLineColor?:
    | LCColorValue<DecorationColors>
    | ConditionalValueFilters<LCColorValue<DecorationColors>>;
}

export interface IconTooltipDescriptionConfig {
  variant: typeof DescriptionVariant.IconTooltip;
  /** icon registry key – defaults to 'InfoCircle' */
  iconName?: string;
  /** icon color — works the same way as the Icon block's transparent.color */
  iconColor?:
    | LCColorValue<TypographyColors>
    | ConditionalValueFilters<LCColorValue<TypographyColors>>;
}

export type DescriptionConfig =
  | InlineDescriptionConfig
  | DashedTooltipDescriptionConfig
  | IconTooltipDescriptionConfig;
